import {
	HttpClient,
	HttpErrorResponse,
	HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
	AUTH_API_BASE_URL,
	AUTH_SUPABASE_CLIENT,
	AuthStateService,
} from '@pressteaze/auth';
import type {
	OperatorContext,
	OperatorDashboard,
	OperatorReservation,
	OperatorReservationInput,
	OperatorReservationUpdate,
	ReservationStatus,
} from '@pressteaze/domain';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { firstValueFrom } from 'rxjs';
import { OperatorAccessTokenProvider } from '../../operator-access-token.provider';

interface ContextResponse {
	readonly session: {
		readonly user_id: string;
		readonly full_name: string;
		readonly store_id: string;
		readonly store_name: string;
	};
	readonly store: {
		readonly id: string;
		readonly name: string;
		readonly timezone: string;
		readonly seating_duration_min: number;
	};
	readonly stores: readonly { readonly id: string; readonly name: string }[];
	readonly areas: readonly {
		readonly id: string;
		readonly name: string;
		readonly sort_order: number;
	}[];
	readonly tables: readonly {
		readonly id: string;
		readonly area_id: string;
		readonly name: string;
		readonly seats: number;
	}[];
}

interface ReservationRow {
	readonly reservation_id: string;
	readonly code: string;
	readonly status: OperatorReservation['status'];
	readonly source: OperatorReservation['source'];
	readonly starts_at: string;
	readonly ends_at: string;
	readonly service_date: string;
	readonly party_size: number;
	readonly table_id: string;
	readonly table_name: string;
	readonly area_id: string;
	readonly area_name: string;
	readonly customer_name: string | null;
	readonly customer_phone: string | null;
	readonly customer_email: string | null;
	readonly customer_notes: string | null;
	readonly operator_notes: string | null;
	readonly version: number;
}

interface DashboardSummaryRow {
	readonly today_reservation_count: number;
	readonly today_guest_count: number;
	readonly today_pending_count: number;
	readonly today_first_starts_at: string | null;
	readonly week_reservation_count: number;
	readonly week_guest_count: number;
	readonly week_no_show_count: number;
	readonly week_online_count: number;
	readonly week_phone_count: number;
	readonly week_walk_in_count: number;
}

interface DashboardResponse {
	readonly today: string;
	readonly week_start: string;
	readonly week_end: string;
	readonly summary: DashboardSummaryRow;
	readonly pending_reservations: readonly ReservationRow[];
}

export class OperatorApiError extends Error {
	constructor(readonly code: string) {
		super(code);
		this.name = 'OperatorApiError';
	}
}

@Injectable({ providedIn: 'root' })
export class OperatorApiService {
	private readonly http = inject(HttpClient);
	private readonly baseUrl = inject(AUTH_API_BASE_URL);
	private readonly supabase = inject(AUTH_SUPABASE_CLIENT);
	private readonly authState = inject(AuthStateService);
	private readonly accessTokenProvider = inject(OperatorAccessTokenProvider);

	async getContext(): Promise<OperatorContext> {
		const response = await this.post<ContextResponse>({ action: 'context' });
		return {
			storeId: response.store.id,
			storeName: response.store.name,
			timezone: response.store.timezone,
			seatingDurationMinutes: response.store.seating_duration_min,
			userId: response.session.user_id,
			userName: response.session.full_name,
			stores: response.stores,
			areas: response.areas.map((area) => ({
				id: area.id,
				name: area.name,
				sortOrder: area.sort_order,
			})),
			tables: response.tables.map((table) => ({
				id: table.id,
				areaId: table.area_id,
				name: table.name,
				seats: table.seats,
			})),
		};
	}

	async getDay(
		serviceDate: string,
		search: string,
	): Promise<readonly OperatorReservation[]> {
		const response = await this.post<{ reservations: ReservationRow[] }>({
			action: 'day',
			service_date: serviceDate,
			search,
		});
		return response.reservations.map((row) => this.mapReservation(row));
	}

	async getAvailableTableIds(
		partySize: number,
		startsLocal: string,
		areaId: string | null,
		excludeReservationId: string | null,
	): Promise<readonly string[]> {
		const response = await this.post<{
			available_table_ids: readonly string[];
		}>({
			action: 'availability',
			party_size: partySize,
			starts_local: startsLocal,
			area_id: areaId,
			exclude_reservation_id: excludeReservationId,
		});
		return response.available_table_ids;
	}

	async getTimeOptions(localDate: string): Promise<readonly string[]> {
		const response = await this.post<{
			readonly time_options: readonly string[];
		}>({
			action: 'time-options',
			local_date: localDate,
		});
		return response.time_options;
	}

	async getDashboard(
		today: string,
		weekStart: string,
		weekEnd: string,
	): Promise<OperatorDashboard> {
		const response = await this.post<DashboardResponse>({
			action: 'dashboard',
			today,
			week_start: weekStart,
			week_end: weekEnd,
		});
		return {
			today: response.today,
			todayReservationCount: response.summary.today_reservation_count,
			todayGuestCount: response.summary.today_guest_count,
			todayPendingCount: response.summary.today_pending_count,
			todayFirstStartsAt: response.summary.today_first_starts_at,
			stats: {
				fromDate: response.week_start,
				toDate: response.week_end,
				reservationCount: response.summary.week_reservation_count,
				guestCount: response.summary.week_guest_count,
				noShowCount: response.summary.week_no_show_count,
				onlineCount: response.summary.week_online_count,
				phoneCount: response.summary.week_phone_count,
				walkInCount: response.summary.week_walk_in_count,
			},
			pendingReservations: response.pending_reservations.map((row) =>
				this.mapReservation(row),
			),
		};
	}

	async createReservation(input: OperatorReservationInput): Promise<void> {
		await this.post({
			action: 'create',
			source: input.source,
			customer_name: input.customerName,
			customer_phone: input.customerPhone,
			customer_email: input.customerEmail,
			party_size: input.partySize,
			starts_local: input.startsLocal,
			area_id: input.areaId,
			table_id: input.tableId,
			customer_notes: input.customerNotes,
			operator_notes: input.operatorNotes,
			idempotency_key: crypto.randomUUID(),
		});
	}

	async updateReservation(input: OperatorReservationUpdate): Promise<void> {
		await this.post({
			action: 'update',
			reservation_id: input.reservationId,
			expected_version: input.expectedVersion,
			customer_name: input.customerName,
			customer_phone: input.customerPhone,
			customer_email: input.customerEmail,
			party_size: input.partySize,
			starts_local: input.startsLocal,
			area_id: input.areaId,
			table_id: input.tableId,
			customer_notes: input.customerNotes,
			operator_notes: input.operatorNotes,
		});
	}

	async transitionReservation(
		reservationId: string,
		expectedVersion: number,
		status: ReservationStatus,
	): Promise<void> {
		await this.post({
			action: 'transition',
			reservation_id: reservationId,
			expected_version: expectedVersion,
			status,
		});
	}

	async connectRealtime(
		storeId: string,
		onChange: () => void,
		onStatusChange: (connected: boolean) => void,
	): Promise<() => Promise<void>> {
		await this.accessTokenProvider.getAccessToken();
		const channel: RealtimeChannel = this.supabase
			.channel(`operator-reservations:${storeId}`)
			.on(
				'postgres_changes',
				{
					event: '*',
					schema: 'public',
					table: 'reservation_realtime_events',
				},
				() => onChange(),
			)
			.subscribe((status) => {
				if (status === 'SUBSCRIBED') onStatusChange(true);
				else if (
					status === 'CHANNEL_ERROR' ||
					status === 'TIMED_OUT' ||
					status === 'CLOSED'
				)
					onStatusChange(false);
			});

		return async () => {
			try {
				await this.supabase.removeChannel(channel);
			} finally {
				this.accessTokenProvider.clear();
			}
		};
	}

	private async post<T>(
		body: object,
		endpoint = 'operator-reservations',
	): Promise<T> {
		try {
			return await firstValueFrom(
				this.http.post<T>(`${this.baseUrl}/${endpoint}`, body, {
					headers: this.headers(),
				}),
			);
		} catch (error) {
			if (error instanceof HttpErrorResponse) {
				const code = (error.error as { code?: string } | null)?.code;
				throw new OperatorApiError(code ?? 'OPERATOR_UNAVAILABLE');
			}
			throw error;
		}
	}

	private headers(): HttpHeaders {
		const token = this.authState.operatorToken();
		return token
			? new HttpHeaders({ Authorization: `Bearer ${token}` })
			: new HttpHeaders();
	}

	private mapReservation(row: ReservationRow): OperatorReservation {
		return {
			id: row.reservation_id,
			code: row.code,
			status: row.status,
			source: row.source,
			startsAt: row.starts_at,
			endsAt: row.ends_at,
			serviceDate: row.service_date,
			partySize: row.party_size,
			tableId: row.table_id,
			tableName: row.table_name,
			areaId: row.area_id,
			areaName: row.area_name,
			customerName: row.customer_name,
			customerPhone: row.customer_phone,
			customerEmail: row.customer_email,
			customerNotes: row.customer_notes,
			operatorNotes: row.operator_notes,
			version: row.version,
		};
	}
}
